import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable/Clickable";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import X from "@insite/mobius/Icons/X";
import { Icon, TextField, Typography } from "@insite/mobius/index";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { css } from "styled-components";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import { COLORS } from "@tKgpco/theme/design";
import Info from "@insite/mobius/Icons/Info";

export interface Option {
    value: string;
    label: string;
}

interface MultiSelectDropdownProps {
    label?: string;
    selectedOptions: Option[];
    options: Option[];
    handleOptionClick: (option: Option) => void;
    placeHolder?: string;
    isDisabled?: boolean;
    isSearchEnabled?: boolean;
    handleSearchChange?: (searchText: string) => void;
    searchPlaceHolder?: string;
    tooltipText?: string;
}

interface MultiSelectDropdownStyles {
    container?: InjectableCss;
    dropDownToggle?: InjectableCss;
    selectedValueContainer?: InjectableCss;
    selectedValue?: InjectableCss;
    selectedValueLabel?: InjectableCss;
    selectedValueRemoveBtn?: InjectableCss;
    label?: TypographyPresentationProps;
    dropDownMenu?: InjectableCss;
    tooltip?: TooltipPresentationProps;
}

const styles: MultiSelectDropdownStyles = {
    container: {
        css: css`
            position: relative;
        `,
    },
    dropDownToggle: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0px 8px;
            padding-left: 0px;
            border: 1px solid #ccc;
            border-radius: 4px;
            cursor: pointer;
            width: 100%;
            margin-top: 10px;
            min-height: 40px;

            &:disabled {
                background: rgb(233, 233, 233);
                pointer-events: none;
            }
        `,
    },
    label: {
        css: css`
            font-weight: 600;
            color: #0e0e0e;
            font-size: 15px;
        `,
    },
    selectedValueContainer: {
        css: css`
            -webkit-box-align: center;
            align-items: center;
            display: flex;
            flex: 1 1 0%;
            flex-wrap: wrap;
            position: relative;
            overflow: hidden;
            padding: 2px 8px;
            box-sizing: border-box;
        `,
    },
    selectedValue: {
        css: css`
            display: flex;
            min-width: 0px;
            background-color: rgba(0, 0, 0, 0.12);
            border-radius: 2px;
            margin: 2px;
            box-sizing: border-box;
        `,
    },
    selectedValueLabel: {
        css: css`
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            border-radius: 2px;
            color: ${getColor("text.main")};
            font-size: 85%;
            padding: 3px 3px 3px 6px;
            box-sizing: border-box;
        `,
    },
    selectedValueRemoveBtn: {
        css: css`
            display: flex;
            align-items: center;
            padding: 3px;
            &:hover {
                background-color: #b3b3b3;
            }
        `,
    },
    dropDownMenu: {
        css: css`
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            max-height: 200px;
            overflow-y: auto;
            border: 1px solid #ccc;
            border-top: none;
            border-radius: 0 0 4px 4px;
            background-color: #fff;
            z-index: 999;
            box-shadow:
                rgba(0, 0, 0, 0.12) 0px 1px 3px,
                rgba(0, 0, 0, 0.24) 0px 1px 2px;
            .option {
                display: flex;
                align-items: center;
                padding: 8px 10px;
                cursor: pointer;
            }
            .option:hover {
                background: rgba(11, 112, 183, 0.19999999999999996);
                color: #000000;
            }
            .option.selected {
                background-color: ${getColor("primary")};
                color: #fff;
            }
        `,
    },
    tooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 7px;
                top: 2px;
                [class^="StyledButton"] {
                    padding: 0px;
                }
            `,
            tooltipBody: css`
                background-color: #0e0e0e;
            `,
        },
        iconProps: {
            src: Info,
            css: css`
                svg {
                    color: #0e0e0e;
                    width: 18px;
                    height: 18px;
                    background-color: #f1f6fb;
                }
            `,
        },
    },
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    options,
    handleOptionClick,
    selectedOptions,
    label,
    placeHolder,
    isDisabled = false,
    isSearchEnabled = false,
    handleSearchChange,
    searchPlaceHolder,
    tooltipText,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: any) {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [ref, setIsOpen]);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const isSelected = (option: Option) => {
        return selectedOptions.some(selectedOption => selectedOption.value === option.value);
    };

    const onChange = (option: Option) => {
        handleOptionClick(option);
    };

    const removeItem = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, option: Option) => {
        e.preventDefault();
        e.stopPropagation();
        handleOptionClick(option);
    };

    return (
        <StyledWrapper {...styles.container} ref={ref}>
            {label && (
                <Typography as="label" htmlFor={label} {...styles.label}>
                    {label}
                    {tooltipText && <Tooltip text={tooltipText} {...styles.tooltip} />}
                </Typography>
            )}
            <Clickable onClick={toggleDropdown} {...styles.dropDownToggle} id={label} disabled={isDisabled}>
                <StyledWrapper {...styles.selectedValueContainer}>
                    {selectedOptions.length > 0 ? (
                        selectedOptions.map(opt => (
                            <StyledWrapper key={opt.label} {...styles.selectedValue}>
                                <StyledWrapper {...styles.selectedValueLabel}>{opt.label}</StyledWrapper>
                                <StyledWrapper
                                    role="button"
                                    aria-label={`Remove ${opt.label}`}
                                    {...styles.selectedValueRemoveBtn}
                                    onClick={e => removeItem(e, opt)}
                                >
                                    <Icon src={X} size={12} />
                                </StyledWrapper>
                            </StyledWrapper>
                        ))
                    ) : (
                        <Typography
                            css={css`
                                font-size: 15px;
                            `}
                        >
                            {placeHolder || "Select Options"}
                        </Typography>
                    )}
                </StyledWrapper>
                <Icon src={isOpen ? ChevronUp : ChevronDown} size={20} />
            </Clickable>
            {isOpen && isSearchEnabled && (
                <TextField
                    type="text"
                    placeholder={searchPlaceHolder || "Search..."}
                    onChange={e => handleSearchChange && handleSearchChange(e.currentTarget.value)}
                />
            )}
            {isOpen && options.length !== selectedOptions.length && (
                <StyledWrapper {...styles.dropDownMenu}>
                    {options.map(option => (
                        <>
                            {!isSelected(option) ? (
                                <div
                                    key={option.value}
                                    className={`option ${isSelected(option) ? "selected" : ""}`}
                                    onClick={() => onChange(option)}
                                >
                                    {option.label}
                                </div>
                            ) : null}
                        </>
                    ))}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default MultiSelectDropdown;
